/**
 * URL slug derivation + validation — ONE implementation, two consumers.
 *
 * Extracted from `<CreateWorkspaceDialog>` when `<NewArticleDialog>` became the
 * second consumer (the workspace slug and the article-unit slug obey the SAME
 * server contract: lowercase `[a-z0-9-]`, no leading/trailing `-`, ≤ 64 chars,
 * `UNIQUE` in its scope). Keeping two copies is how the two drift and one of
 * them starts 409-ing on a name the other accepts.
 *
 * The client half is a PREVIEW + an early reject, never the authority: the
 * server derives/validates the slug and owns the uniqueness verdict (409).
 * `CreateWorkspaceDialog` re-exports these so its existing importers (and
 * `api/workspaceMgmt.test.ts`) keep working unchanged.
 */

/** Derive a url-safe slug from a display name (mirrors the server's
 *  `isValidSlug`: lowercase `[a-z0-9-]`, no leading/trailing '-'). Returns `""`
 *  when the name has no ASCII-alphanumeric content at all (e.g. "Москва") — the
 *  caller must then ask for an explicit slug rather than post an empty one. */
export function slugify(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 64)
    .replace(/-+$/g, "");
}

/** Client-side slug validity — matches the server contract so we reject early. */
export function isValidSlug(slug: string): boolean {
  return /^[a-z0-9]([a-z0-9-]{0,62}[a-z0-9])?$/.test(slug) || /^[a-z0-9]$/.test(slug);
}
